import { FormContainer, LabeledInput, Worksheet } from "uikit"
import HumanTimeSince from "components/HumanTimeSince"
import React from "react"
import { Dropdown, Grid } from "semantic-ui-react"
import { useStore } from "store"
import { AddPersonModal } from "../../../people/AddPersonModal"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import { Footer } from "./Footer"

const RothRetirementAccountForm = ({
  data: {
    fields: { person_id, balance, show_advanced_options, basis, year_opened },
    isModified,
    onSubmit,
    error
  },
  submitLabel = "Done",
  ownerOptions,
  isNew,
  onDelete,
  openedYearOptions,
  addNewPerson,
  lastUpdatedAt,
  account
}) => {
  const canHideAdvancedOptions =
    !parseInt(basis.value, 10) &&
    year_opened.value === new Date().getFullYear() + ""
  return (
    <Grid>
      {account.connection_id ? (
        <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
          <Grid.Column>
            <ConnectedAccountBar {...account} />
          </Grid.Column>
        </Grid.Row>
      ) : null}
      <Grid.Row>
        <Grid.Column>
          <Worksheet error style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row
                label="What is the current balance of this account?"
                description={
                  account.connection_id
                    ? "Automatically updated through the account connection"
                    : null
                }
                paddedField
              >
                <Worksheet.Field error={balance.error}>
                  <LabeledInput
                    {...balance}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    disabled={!!account.connection_id}
                    data-testid="balance"
                  />
                  {lastUpdatedAt}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Who is the account holder/owner?"
                paddedField
              >
                <Worksheet.Field error={person_id.error}>
                  <Dropdown
                    {...person_id}
                    placeholder="Account Holder"
                    options={ownerOptions}
                    selection
                    data-testid="accountOwner"
                  />
                  {person_id.value === "__ADD__" && (
                    <AddPersonModal
                      submit={personValues =>
                        addNewPerson(personValues).then(({ personId }) =>
                          person_id.onChange(null, { value: personId })
                        )
                      }
                      onCancel={() => person_id.onChange(null, { value: null })}
                    />
                  )}
                </Worksheet.Field>
              </Worksheet.Row>
              {show_advanced_options.checked && (
                <Worksheet.Row
                  label="How much have you contributed to this account?"
                  description="Your total contributions over the years, not including subsequent investment gains on those contributions."
                  paddedField
                  advancedField
                >
                  <Worksheet.Field error={basis.error}>
                    <LabeledInput
                      {...basis}
                      label=""
                      iconText="$"
                      iconPosition="left"
                      placeholder="0"
                      data-testid="basis"
                      // onChange={event => {
                      //   let value = event.target.value
                      //   if (!value) {
                      //     value = ''
                      //   }
                      //   basis.onChange(event, { value })
                      // }}
                    />
                  </Worksheet.Field>
                </Worksheet.Row>
              )}
              {show_advanced_options.checked && (
                <Worksheet.Row
                  label="What year was this account first opened and funded?"
                  paddedField
                  advancedField
                >
                  <Worksheet.Field error={year_opened.error}>
                    <Dropdown
                      selection
                      {...year_opened}
                      fluid
                      placeholder="Year"
                      options={openedYearOptions}
                    />
                  </Worksheet.Field>
                </Worksheet.Row>
              )}
            </Worksheet.Body>
            {!show_advanced_options.checked && (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <span
                    style={{ color: " #00b8ee", cursor: "pointer" }}
                    onClick={ev =>
                      show_advanced_options.onChange(ev, { value: true })
                    }
                    data-testid="showAdvanced"
                  >
                    Show Advanced Options
                  </span>
                </Grid.Column>
              </Grid.Row>
            )}
            {show_advanced_options.checked && canHideAdvancedOptions && (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <span
                    style={{ color: " #00b8ee", cursor: "pointer" }}
                    onClick={ev =>
                      show_advanced_options.onChange(ev, { value: false })
                    }
                  >
                    Hide Advanced Options
                  </span>
                </Grid.Column>
              </Grid.Row>
            )}

            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel={
                account.connection_id ? "Ignore Account" : "Delete Account"
              }
              hasConnectionId={account.connection_id ? true : false}
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const RothRetirementFormContainer = FormContainer(
  {
    balance: {
      type: "unit",
      required: true
    },
    person_id: {
      type: "string"
    },
    show_advanced_options: {
      type: "boolean",
      required: false,
      submit: false
    },
    basis: {
      type: "unit",
      required: false
    },
    year_opened: {
      type: "integer",
      useGrouping: false,
      required: false,
      defaultValue: new Date().getFullYear()
    }
  },
  (fields, ...form) => {}
)(RothRetirementAccountForm)

const RothRetirementAccount = ({ household, account, ...formProps }) => {
  const isNew = account == null
  const [people, { addPerson }] = useStore("householdPeople", household.id)
  const ownerOptions = Object.values(people)
    .filter(person => person.role === "spouse" || person.role === "client")
    .map(({ id: value, first_name: text }) => ({ value, text }))
  const defaultOwnerId = ownerOptions.length === 1 ? ownerOptions[0].value : ""
  let values = isNew ? { person_id: defaultOwnerId } : account
  const canAddOwner = false
  const currentYear = new Date().getFullYear()
  const openedYearOptions = Array(6)
    .fill(0)
    .map((_, index) => {
      const year = currentYear - index + ""
      return {
        text: `${year}${index === 5 ? " or before" : ""}`,
        value: year
      }
    })
  if (!isNew) {
    let show_advanced_options =
      !!values.basis || values.year_opened !== new Date().getFullYear()
    values = Object.assign({}, account, { show_advanced_options })
  }
  const lastUpdatedAt = !isNew ? (
    <p
      style={{
        fontSize: "0.8em",
        textAlign: "right",
        fontWeight: "normal"
      }}
    >
      Updated <HumanTimeSince time={values.balance_updated_at} /> ago
    </p>
  ) : null
  if (canAddOwner) {
    ownerOptions.push({ text: "Add person...", value: "__ADD__" })
  }
  const addNewPerson = values =>
    addPerson({ household_id: household.id, ...values })
  return (
    <RothRetirementFormContainer
      {...formProps}
      ownerOptions={ownerOptions}
      values={values}
      addNewPerson={addNewPerson}
      account={values}
      lastUpdatedAt={lastUpdatedAt}
      openedYearOptions={openedYearOptions}
    />
  )
}

export default RothRetirementAccount
